import React, { useCallback, useRef, useState } from 'react';
import { Pressable, View } from 'react-native';

import { Box, Icon, PressableIcon, Text } from '@bonanza-social/moonlight';
import {
  type Lang,
  useOutsidePress,
} from '../../../hooks/useLanguageSwitcherHooks';

type LanguageInlineSelectorProps = {
  lang: Lang;
  onSelect: (lang: Lang) => void;
};

const options: { label: string; value: Lang }[] = [
  { label: 'FR', value: 'fr' },
  { label: 'EN', value: 'en' },
];

function LanguageInlineSelector({
  lang,
  onSelect,
}: LanguageInlineSelectorProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hovered, setHovered] = useState<Lang | null>(null);
  const dropdownRef = useRef<View>(null);

  const selectedLabel = options.find((o) => o.value === lang)?.label ?? 'EN';

  const closeDropdown = useCallback(() => {
    setDropdownOpen(false);
    setHovered(null);
  }, []);

  useOutsidePress(dropdownOpen, dropdownRef, closeDropdown);

  return (
    <Box padding="space-025" alignSelf="center">
      <Box
        flexDirection="row"
        gap="space-100"
        centerContent
        backgroundColor="primaryBackground"
        borderStyle="solid"
        borderWidth="border-width-025"
        borderRadius="radius-200"
        borderColor="borderGoldSolid"
        paddingHorizontal="space-200"
        flex={1}
      >
        <Pressable onPress={() => setDropdownOpen((p) => !p)}>
          <Text
            variant="body-s"
            color="darkSurfaceAccentSolid"
            locNS="common"
            locKey="language"
          />
        </Pressable>

        <Box ref={dropdownRef} position="relative">
          <Pressable onPress={() => setDropdownOpen((p) => !p)}>
            <Box
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              padding="space-200"
              borderRadius="radius-100"
              paddingLeft="space-200"
              paddingRight="space-200"
              paddingTop="space-200"
              paddingBottom="space-200"
              cursor="pointer"
              minWidth={40}
            >
              <Text variant="body-s" color="darkSurfaceAccentSolid">
                {selectedLabel}
              </Text>

              <PressableIcon
                name="arrowDropDown"
                gradient="accentBackground"
                width={12}
                height={17}
                onPress={() => setDropdownOpen((p) => !p)}
              />
            </Box>
          </Pressable>

          {dropdownOpen && (
            <Box
              position="absolute"
              top="-space-200"
              minWidth={100}
              zIndex={1000}
              left="space-1600"
            >
              <Box
                marginTop="space-100"
                backgroundColor="iconPrimary"
                borderRadius="radius-200"
                borderBlockColor="borderSolidGold"
                padding="space-100"
                boxShadow="0 8px 24px rgba(0,0,0,0.35)"
              >
                {options.map((option) => {
                  const isActive = option.value === lang;
                  const isHovered = hovered === option.value;

                  return (
                    <Pressable
                      key={option.value}
                      onPress={() => {
                        onSelect(option.value);
                        setDropdownOpen(false);
                        setHovered(null);
                      }}
                      onHoverIn={() => setHovered(option.value)}
                      onHoverOut={() => setHovered(null)}
                    >
                      <Box
                        flexDirection="row"
                        justifyContent="space-between"
                        alignItems="center"
                        padding="space-200"
                        borderRadius="radius-100"
                        backgroundColor={
                          isHovered ? 'secondaryBackgroundHover' : 'transparent'
                        }
                        cursor="pointer"
                      >
                        <Text
                          variant="body-s"
                          color={
                            isActive
                              ? 'darkSurfaceAccentSolid'
                              : 'darkSurfacePrimary'
                          }
                        >
                          {option.label}
                        </Text>
                        {isActive && (
                          <Icon
                            name="check"
                            color="darkSurfaceAccentSolid"
                            width={16}
                            height={16}
                          />
                        )}
                      </Box>
                    </Pressable>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default LanguageInlineSelector;
